<script setup lang="ts">
// Renders a formatted string with every currency code swapped for the
// currency mark: "AED 1,200 / yr" → [mark]1,200 / yr. Formatters and
// builders keep producing plain strings (they also feed aria-labels, CSV,
// tests); only the on-screen render goes through here.
//
// Renders a fragment — no wrapper element — so it drops inside any <Text>
// and inherits its size and colour:
//   <Text type="heading"><CurrencyText :text="row.value" /></Text>
import { computed } from 'vue'
import { CURRENCY_CODE } from '@shared/utils'
import CurrencySymbol from './CurrencySymbol.vue'

interface Props {
  /** Any string; each standalone currency code in it becomes the mark. */
  text?: string | number | null
}

const props = defineProps<Props>()

// Standalone code only — "EBILAED" (a SWIFT code) stays untouched. The
// trailing space goes too; the mark carries its own spacing.
const CODE_PATTERN = new RegExp(`\\b${CURRENCY_CODE}\\b ?`)

const parts = computed(() => String(props.text ?? '').split(CODE_PATTERN))
</script>

<template>
  <!-- `><` hugging on purpose: a plain line break would render as a stray space. -->
  <template v-for="(part, index) in parts" :key="index"
    ><CurrencySymbol v-if="index > 0" />{{ part }}</template
  >
</template>
